import {
  ApiError,
  CancelablePromise,
  CommunicationPage,
  MessageDirection,
  MessageResponse,
  MessagesService,
  OrganizationResponseForAdmins,
} from "client/openapi";
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { PageStatus } from "types";
import { ChevronDownIcon } from "@radix-ui/react-icons";
import moment from "moment";

import PersonPill from "components/PersonPill";
import { MessageDeliveryTag } from "components/MessageDeliveryTag";
import { ChannelFilter } from "./CommunicationFilters";
import Notifications from "util/notifications";
import { Marks, useUnread } from "util/UnreadContext";
import UnknownSenderDialog from "components/LinkUnknownContact/UnknownSenderDialog";
import { UnknownContactInfo } from "components/LinkUnknownContact/UnknownContactDialog";
import MessageBubble from "./MessageBubble";
import PrivateMark from "./PrivateMark";
import ReadToggle from "./ReadToggle";
import ReplyBox from "./ReplyBox";
import SendersToggle, { Senders } from "./SendersToggle";
import { taskPeople } from "components/Dashboard/ManageTasksDialog/PeoplePicker";
import { EmailRecipient } from "./EmailToLine";
import {
  answering,
  groupRows,
  isEmail,
  isInbound,
  isQuiet,
  isUnknownSender,
  pickEmail,
  Picked,
  rowChannel,
  newestInbound,
  suggestionFor,
  unknownAddress,
  unreadInbound,
} from "./conversations";
import {
  Cursor,
  Feed,
  feedMessages,
  firstPage,
  withFirstPage,
  withOlderPage,
} from "./feed";
import "components/LinkUnknownContact/index.css";

type TaggedUser =
  | {
      id: number;
      first_name: string;
      last_name: string;
    }
  | null
  | undefined;

export default function OrgCommunication({
  organization,
  startDate,
  endDate,
  directionFilter = "all",
  channelFilter = "all",
  controls,
  openRequest,
  scrollRoot,
}: {
  organization: OrganizationResponseForAdmins;
  startDate: Date;
  endDate: Date;
  directionFilter?: "all" | MessageDirection;
  channelFilter?: ChannelFilter;
  /** Where the Known / Unknown toggle and Mark all read go (the filters row). */
  controls?: HTMLElement | null;
  /** A row to open (a link to a conversation): reloads, then opens it, each time. */
  openRequest?: { row: string; at: number } | null;
  /** The list's scroll box: older messages load as its end comes into view. */
  scrollRoot?: HTMLElement | null;
}) {
  // What is loaded; null until the first page of these dates is in.
  const [feed, setFeed] = useState<Feed | null>(null);
  const [status, setStatus] = useState<PageStatus>(PageStatus.LOADING);
  const [error, setError] = useState<ApiError>();
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  // The email the open row's reply answers (none picked: the newest).
  const [picked, setPicked] = useState<Picked>(null);
  // Bumped when Reply is clicked, to put the cursor in the reply box.
  const [replyFocus, setReplyFocus] = useState(0);
  const [senders, setSenders] = useState<Senders>("known");
  const [unknownContact, setUnknownContact] =
    useState<UnknownContactInfo | null>(null);
  const [hasMoreAbove, setHasMoreAbove] = useState(false);
  const { setRead, lastChange, polled } = useUnread();
  // A quiet reload (no Loading screen) is in flight.
  const [refreshing, setRefreshing] = useState(false);
  // An older page is loading.
  const [loadingOlder, setLoadingOlder] = useState(false);
  // The end of the list is in view, or nearly.
  const [atEnd, setAtEnd] = useState(false);
  const [end, setEnd] = useState<HTMLDivElement | null>(null);
  // A linked conversation to open once it is loaded.
  const [pendingOpen, setPendingOpen] = useState<string | null>(null);
  const latestFetch = useRef(0);
  // One request at a time: the one in flight, cancelled when another
  // replaces it.
  const inFlight = useRef<CancelablePromise<CommunicationPage>>();

  // Add ref for the messages container
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom when a conversation opens
  useEffect(() => {
    if (expandedRow && messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      container.scrollTop = container.scrollHeight;
      setHasMoreAbove(container.scrollHeight > container.clientHeight);
    }
  }, [expandedRow]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    setHasMoreAbove(el.scrollTop > 0);
  };

  // A page of these dates' messages: the newest, or those older than the
  // cursor. The request it replaces is cancelled, so the browser drops an
  // answer no one will see.
  const requestPage = useCallback(
    (cursor?: Cursor) => {
      inFlight.current?.cancel();
      const request = MessagesService.getOrganizationCommunicationPage({
        orgId: organization.id,
        startDate: moment(startDate).startOf("day").toISOString(),
        endDate: moment(endDate).endOf("day").toISOString(),
        before: cursor?.before,
        beforeId: cursor?.beforeId,
      });
      inFlight.current = request;
      return request;
    },
    [organization.id, startDate, endDate]
  );

  // Leaving the view cancels what is in flight.
  useEffect(
    () => () => {
      latestFetch.current++;
      inFlight.current?.cancel();
    },
    []
  );

  // The first page. A quiet reload keeps the list (and any open dialog) on
  // screen and replaces only the part the first page covers, unless it
  // starts over (a sender linked: their older messages change rows too);
  // other dates start over, from the top, and drop a link still being
  // looked for.
  const fetchMessages = useCallback(
    async (quiet = false, startOver = false) => {
      const request = ++latestFetch.current;
      if (quiet) setRefreshing(true);
      else {
        setStatus(PageStatus.LOADING);
        setFeed(null);
        setAtEnd(false);
        setPendingOpen(null);
      }
      setLoadingOlder(false);

      try {
        const page = await requestPage();
        if (request !== latestFetch.current) return;
        setFeed((prev) =>
          prev && !startOver
            ? withFirstPage(
                prev,
                page,
                moment(startDate).startOf("day").toDate()
              )
            : firstPage(page)
        );
        setStatus(PageStatus.SUCCESS);
      } catch (e: any) {
        if (request !== latestFetch.current) return;
        console.error(`Error: ${e.message}`);
        if (!quiet) {
          setStatus(PageStatus.ERROR);
          setError(e);
        }
      } finally {
        if (request === latestFetch.current) setRefreshing(false);
      }
    },
    [requestPage, startDate]
  );

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // The next page, at the end of the list.
  const loadOlder = useCallback(
    async (cursor: Cursor) => {
      const request = ++latestFetch.current;
      setLoadingOlder(true);

      try {
        const page = await requestPage(cursor);
        if (request !== latestFetch.current) return;
        setFeed((prev) => prev && withOlderPage(prev, page));
      } catch (e: any) {
        if (request !== latestFetch.current) return;
        console.error(`Error: ${e.message}`);
        setError(e);
        setPendingOpen(null);
      } finally {
        if (request === latestFetch.current) {
          setLoadingOlder(false);
          // Whether the end is still in view is watched afresh, so a page
          // that failed isn't asked for again until it comes back into view.
          setAtEnd(false);
        }
      }
    },
    [requestPage]
  );

  // Read marks show at once.
  useEffect(() => {
    if (!lastChange) return;
    const ids = new Set(lastChange.messageIds);
    const marked = (list: MessageResponse[]) =>
      list.map((m) =>
        isInbound(m) && ids.has(m.id) ? { ...m, seen: lastChange.read } : m
      );
    setFeed(
      (prev) =>
        prev && {
          ...prev,
          messages: marked(prev.messages),
          unread: marked(prev.unread),
        }
    );
  }, [lastChange]);

  const rows = useMemo(() => groupRows(feedMessages(feed)), [feed]);

  // Everyone an email reply can be addressed to (archived people left out).
  const contacts = useMemo(() => {
    const current = <T extends { archived_at?: string | null }>(people: T[]) =>
      people.filter((person) => !person.archived_at);
    return taskPeople({
      tutors: current(organization.tutors ?? []) as any,
      parents: current(organization.parents ?? []) as any,
      students: current(organization.students ?? []) as any,
    }) as EmailRecipient[];
  }, [organization]);

  // Rows that count as unread, by side of the Known / Unknown toggle.
  const unreadRows = rows.filter(
    ([row, msgs]) => !isQuiet(row, msgs) && unreadInbound(msgs).length > 0
  );
  const unreadKnown = unreadRows.filter(([row]) => !isUnknownSender(row));
  const unreadUnknown = unreadRows.filter(([row]) => isUnknownSender(row));

  // Keep a row if it's on this side of the toggle and at least one message
  // matches the direction and channel filters. The whole thread shows.
  const shownRows = rows.filter(
    ([row, msgs]) =>
      isUnknownSender(row) === (senders === "unknown") &&
      msgs.some((msg) => {
        const dirOk =
          directionFilter === "all" || msg.direction === directionFilter;
        const chanOk =
          channelFilter === "all"
            ? true
            : channelFilter === "sms"
            ? msg.sms
            : channelFilter === "email"
            ? msg.email
            : !msg.sms && !msg.email;
        return dirOk && chanOk;
      })
  );

  // Something new arrived (the minute's poll): reload quietly.
  const reloadedFor = useRef(polled);
  useEffect(() => {
    if (polled === reloadedFor.current) return;
    reloadedFor.current = polled;
    const timer = window.setTimeout(() => fetchMessages(true), 500);
    return () => window.clearTimeout(timer);
  }, [polled, fetchMessages]);

  // Whether the end of the list is in view, or nearly. Watched afresh as
  // the list changes, so a list too short to scroll (filters hide most of
  // it) keeps loading.
  useEffect(() => {
    if (!end) return;
    const observer = new IntersectionObserver(
      ([entry]) => setAtEnd(entry.isIntersecting),
      { root: scrollRoot ?? null, rootMargin: "0px 0px 300px 0px" }
    );
    observer.observe(end);
    return () => observer.disconnect();
  }, [end, scrollRoot, feed]);

  // The next page loads when the end of the list comes into view, and while
  // a linked conversation isn't loaded yet. One page at a time.
  useEffect(() => {
    const next = feed?.next;
    if (!next || status !== PageStatus.SUCCESS || refreshing || loadingOlder) {
      return;
    }
    if (atEnd || (pendingOpen && !rows.some(([row]) => row === pendingOpen))) {
      loadOlder(next);
    }
  }, [
    feed,
    status,
    refreshing,
    loadingOlder,
    atEnd,
    pendingOpen,
    rows,
    loadOlder,
  ]);

  // A link to a conversation: reload quietly, load older pages until that
  // row is in (it may be an old one), then open it, on the side of the
  // toggle it's on, and mark it read for this admin.
  const handledRequest = useRef<number | null>(null);
  useEffect(() => {
    if (!openRequest || handledRequest.current === openRequest.at) return;
    handledRequest.current = openRequest.at;
    setPendingOpen(openRequest.row);
    fetchMessages(true);
  }, [openRequest, fetchMessages]);
  useEffect(() => {
    if (!pendingOpen || status !== PageStatus.SUCCESS || refreshing) return;
    if (loadingOlder) return;
    const found = rows.find(([row]) => row === pendingOpen);
    // Not loaded yet: the next page is on its way (above).
    if (!found && feed?.next) return;
    setPendingOpen(null);
    if (!found) return;
    const [row, msgs] = found;
    setSenders(isUnknownSender(row) ? "unknown" : "known");
    setExpandedRow(row);
    setPicked(null);
    if (!isQuiet(row, msgs)) setRead({ rows: [row] }, true).catch(() => {});
    window.setTimeout(() =>
      document
        .getElementById(`communication-row-${row}`)
        ?.scrollIntoView({ block: "center" })
    );
  }, [pendingOpen, status, refreshing, loadingOlder, rows, feed, setRead]);

  async function mark(marks: Marks, read: boolean) {
    try {
      await setRead(marks, read);
    } catch {
      Notifications.error("That could not be saved. Try again.");
    }
  }

  // Opening a conversation marks it read for this admin: the whole row as
  // the server knows it, so a message this list hasn't loaded is included.
  function open(row: string, msgs: MessageResponse[]) {
    setExpandedRow(row);
    setPicked(null);
    if (!isQuiet(row, msgs)) mark({ rows: [row] }, true);
  }

  function toggleOpen(row: string, msgs: MessageResponse[]) {
    if (expandedRow === row) setExpandedRow(null);
    else open(row, msgs);
  }

  // The dot: unread to read, or read back to unread (the newest message).
  function toggleRead(row: string, msgs: MessageResponse[]) {
    if (unreadInbound(msgs).length) mark({ rows: [row] }, true);
    else {
      const newest = newestInbound(msgs);
      if (newest) mark({ messageIds: [newest.id] }, false);
    }
  }

  async function markAllRead() {
    const ids = shownRows
      .filter(([row, msgs]) => !isQuiet(row, msgs))
      .flatMap(([, msgs]) => unreadInbound(msgs).map((m) => m.id));
    if (!ids.length) return;
    try {
      const result = await setRead({ messageIds: ids }, true);
      const marked = result?.message_ids ?? ids;
      const count = shownRows.filter(
        ([row, msgs]) => !isQuiet(row, msgs) && unreadInbound(msgs).length
      ).length;
      Notifications.undoable(
        `Marked ${count} ${
          count === 1 ? "conversation" : "conversations"
        } read.`,
        () => mark({ messageIds: marked }, false)
      );
    } catch {
      Notifications.error("That could not be saved. Try again.");
    }
  }

  // Reply (or Reply to it) on an email: answer it, show it as the one being
  // answered, and put the cursor in the reply box.
  function reply(msgs: MessageResponse[], message: MessageResponse) {
    setPicked(pickEmail(msgs, message, true));
    setReplyFocus((n) => n + 1);
  }

  // Link or add a sender who matches no one (their newest message's number
  // or address).
  function linkOrAdd(rowMessages: MessageResponse[]) {
    const sender = newestInbound(rowMessages);
    if (!sender) return;
    setUnknownContact({
      email: sender.sender_email || null,
      phone_number: sender.sender_phone || null,
    });
  }

  // A reply shows in its row at once, and the row is read for whoever sent it.
  function replied(
    row: string,
    msgs: MessageResponse[],
    sent: MessageResponse
  ) {
    setFeed(
      (prev) =>
        prev && {
          ...prev,
          messages: [
            { ...sent, row_key: sent.row_key || row },
            ...prev.messages,
          ],
        }
    );
    setPicked(null);
    mark({ rows: [row] }, true);
  }

  const truncateText = (text: string, maxLength: number) => {
    if (!text) return "";
    return text.length > maxLength
      ? `${text.substring(0, maxLength)}...`
      : text;
  };

  const handleUserClick = (userType: string, userId: number) => {
    window.open(`/manage/${userType}s/${userId}`, "_blank");
  };

  // People as small pills in their role's tint; a long name ends in "…" with
  // the full name on hover. Condensed (the table rows), at most two and
  // "+N more". A sender who matches no one is a pill that links or adds them.
  const renderTags = (
    parents: TaggedUser[] = [],
    students: TaggedUser[] = [],
    tutors: TaggedUser[] = [],
    condense: boolean = true,
    rowMessages: MessageResponse[] = [],
    plainLabel?: string
  ) => {
    const pills = (children: ReactNode) => (
      <div className="flex min-w-0 flex-wrap gap-1">{children}</div>
    );

    // A plain label (the org's or the sender's name).
    if (plainLabel) {
      return pills(<PersonPill label={plainLabel} />);
    }

    const allTags = [
      ...(
        parents?.filter((p): p is NonNullable<TaggedUser> => p != null) || []
      ).map((p) => ({ ...p, type: "parent" as const, color: "blue" as const })),
      ...(
        students?.filter((s): s is NonNullable<TaggedUser> => s != null) || []
      ).map((s) => ({
        ...s,
        type: "student" as const,
        color: "green" as const,
      })),
      ...(
        tutors?.filter((t): t is NonNullable<TaggedUser> => t != null) || []
      ).map((t) => ({ ...t, type: "tutor" as const, color: "gold" as const })),
    ];

    // Nobody on file: the sender's number or email, to link or add them. A
    // row with no sender to link (staff's own email to no one on file) is
    // plain Unknown.
    if (allTags.length === 0) {
      const address = unknownAddress(rowMessages);
      return pills(
        address ? (
          <PersonPill
            label={`Unknown (${address})`}
            title="Link to a student, parent or tutor, or add them"
            onClick={() => linkOrAdd(rowMessages)}
          />
        ) : (
          <PersonPill label="Unknown" />
        )
      );
    }

    const shown = condense ? allTags.slice(0, 2) : allTags;
    return pills(
      <>
        {shown.map((user) => (
          <PersonPill
            key={`${user.type}-${user.id}`}
            label={`${user.first_name} ${user.last_name}`}
            color={user.color}
            onClick={() => handleUserClick(user.type, user.id)}
          />
        ))}
        {allTags.length > shown.length && (
          <PersonPill label={`+${allTags.length - shown.length} more`} />
        )}
      </>
    );
  };

  useEffect(() => {
    if (error) {
      Notifications.error(error.message || "An unexpected error occurred.");
    }
  }, [error]);

  // Known / Unknown, with each side's unread conversations, and Mark all as
  // read (small: it has Undo, but it shouldn't invite a stray click).
  const toolbar = (
    <div className="flex flex-wrap items-center gap-2">
      <span className="mr-1 text-sm font-medium whitespace-nowrap">
        Senders:
      </span>
      <SendersToggle
        value={senders}
        onChange={setSenders}
        unread={{ known: unreadKnown.length, unknown: unreadUnknown.length }}
      />
      {shownRows.some(
        ([row, msgs]) => !isQuiet(row, msgs) && unreadInbound(msgs).length
      ) && (
        <button
          type="button"
          onClick={markAllRead}
          className="ml-2 text-xs text-gray-500 underline-offset-2 hover:text-gray-700 hover:underline"
        >
          Mark all as read
        </button>
      )}
    </div>
  );

  const unknownDialog = (
    <UnknownSenderDialog
      contact={unknownContact}
      orgId={organization.id}
      onChanged={() => fetchMessages(true, true)}
      onClose={() => setUnknownContact(null)}
    />
  );

  // Under the list while older messages remain: a line while a page loads,
  // and the mark whose coming into view loads the next.
  const listEnd = feed?.next && (
    <>
      {loadingOlder && (
        <div className="p-3 text-center text-sm text-gray-500">
          Loading older messages...
        </div>
      )}
      <div ref={setEnd} className="h-px" />
    </>
  );

  if (status === PageStatus.LOADING) {
    return (
      <>
        {controls && createPortal(toolbar, controls)}
        {unknownDialog}
        <div>Loading...</div>
      </>
    );
  }

  if (shownRows.length === 0) {
    return (
      <>
        {controls && createPortal(toolbar, controls)}
        {unknownDialog}
        <div className="p-4 text-gray-500">
          {senders === "unknown"
            ? "No messages from unknown numbers or emails"
            : "No messages"}
        </div>
        {listEnd}
      </>
    );
  }

  return (
    <>
      {controls && createPortal(toolbar, controls)}
      {unknownDialog}
      {/* Fixed layout: the columns keep their shares, so a long name ends in
          "…" inside its pill instead of widening From or To. */}
      <table className="w-full table-fixed bg-white border-collapse">
        <thead className="sticky top-0 bg-white z-1 shadow-sm">
          <tr className="border-b-2 border-gray-300">
            <th className="text-left p-2 text-sm font-bold w-[12.5%]">Date</th>
            <th className="text-left p-2 text-sm font-bold w-[12.5%]">
              Subject
            </th>
            <th className="text-left p-2 text-sm font-bold w-[23%]">Content</th>
            <th className="text-left p-2 text-sm font-bold w-[12.5%]">From</th>
            <th className="text-left p-2 text-sm font-bold w-[12.5%]">To</th>
            <th className="text-center p-2 text-sm font-bold w-[10%]">
              Delivery
            </th>
            <th className="text-center p-2 text-sm font-bold w-[9.5%]">
              Read / unread
            </th>
            <th className="text-center p-2 text-sm font-bold w-[5%]">
              Details
            </th>
          </tr>
        </thead>
        <tbody>
          {shownRows.map(([row, conversationMessages]) => {
            const latestMessage =
              conversationMessages[conversationMessages.length - 1];
            const isExpanded = expandedRow === row;
            const quiet = isQuiet(row, conversationMessages);
            const unread = unreadInbound(conversationMessages).length > 0;
            const hasInbound = conversationMessages.some(isInbound);
            // Internal-log conversations were never texted or emailed, and
            // a sender who matches no one has to be linked or added first,
            // so neither has a reply box.
            const isInternalConversation =
              latestMessage.direction === MessageDirection.OUTBOUND &&
              !latestMessage.sms &&
              !latestMessage.email;
            const canReply = !isInternalConversation && !isUnknownSender(row);
            const { answered, newer } = answering(
              conversationMessages,
              isExpanded ? picked : null
            );
            // Claude's draft, offered while answering the message it was
            // drafted for.
            const suggestion = suggestionFor(conversationMessages);

            return (
              <RowFragment key={row}>
                <tr
                  id={`communication-row-${row}`}
                  className="border-b border-gray-200 hover:bg-gray-50"
                >
                  <td className="p-2 text-[13px]">
                    {moment(latestMessage.created_at).format("MMM Do, h:mm a")}
                  </td>
                  <td className="p-2 text-[13px]">
                    <PrivateMark message={latestMessage} />
                    {truncateText(latestMessage?.subject || "", 25)}
                  </td>
                  <td className="p-2 text-[13px]">
                    {truncateText(latestMessage.content, 75)}
                  </td>
                  <td className="p-2">
                    {latestMessage.direction === MessageDirection.OUTBOUND
                      ? renderTags(
                          [],
                          [],
                          [],
                          true,
                          conversationMessages,
                          latestMessage.sender
                            ? `${latestMessage.sender.first_name} ${latestMessage.sender.last_name}`
                            : latestMessage.sender_email || organization.name
                        )
                      : renderTags(
                          latestMessage.matched_parents || [],
                          latestMessage.matched_students || [],
                          latestMessage.matched_tutors || [],
                          true,
                          conversationMessages
                        )}
                  </td>
                  <td className="p-2">
                    {latestMessage.direction === MessageDirection.OUTBOUND
                      ? renderTags(
                          latestMessage.parent_recipients || [],
                          latestMessage.student_recipients || [],
                          latestMessage.tutor_recipients || [],
                          true,
                          conversationMessages
                        )
                      : renderTags(
                          [],
                          [],
                          [],
                          true,
                          conversationMessages,
                          organization.name
                        )}
                  </td>
                  <td className="p-2 text-center">
                    <MessageDeliveryTag message={latestMessage} />
                  </td>
                  <td className="p-2 text-center">
                    {hasInbound && !quiet && (
                      <ReadToggle
                        unread={unread}
                        onToggle={() => toggleRead(row, conversationMessages)}
                      />
                    )}
                  </td>
                  <td className="p-2 text-center">
                    <button
                      onClick={() => toggleOpen(row, conversationMessages)}
                      className="p-1 hover:bg-gray-100 rounded-full transition-transform"
                      style={{
                        transform: isExpanded
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                      }}
                    >
                      <ChevronDownIcon width="20" height="20" />
                    </button>
                  </td>
                </tr>
                {isExpanded && (
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <td colSpan={8} className="p-4">
                      <div className="space-y-4">
                        <div className="relative">
                          {/* Gradient hint at top */}
                          <div
                            className={`absolute top-0 left-0 right-0 h-6 bg-gradient-to-b from-gray-50 to-transparent pointer-events-none transition-opacity duration-200 ${
                              hasMoreAbove ? "opacity-100" : "opacity-0"
                            }`}
                          />
                          <div
                            className="space-y-4 max-h-[300px] overflow-y-auto p-2 pb-6 border border-gray-200"
                            ref={messagesContainerRef}
                            onScroll={handleScroll}
                          >
                            {conversationMessages.map((message) => (
                              <MessageBubble
                                key={`${row}-${message.id}`}
                                message={message}
                                organizationName={organization.name}
                                onReply={
                                  canReply
                                    ? () => reply(conversationMessages, message)
                                    : undefined
                                }
                                answering={
                                  !!picked?.chosen &&
                                  isEmail(message) &&
                                  answered?.id === message.id
                                }
                                onUnknownSender={
                                  isUnknownSender(row)
                                    ? () => linkOrAdd(conversationMessages)
                                    : undefined
                                }
                              />
                            ))}
                          </div>
                          {canReply && (
                            <div className="mt-4">
                              <ReplyBox
                                row={row}
                                messages={conversationMessages}
                                orgId={organization.id}
                                answered={
                                  rowChannel(conversationMessages) === "email"
                                    ? answered
                                    : undefined
                                }
                                people={contacts}
                                newer={newer}
                                focusKey={replyFocus}
                                suggestion={
                                  suggestion &&
                                  (rowChannel(conversationMessages) !==
                                    "email" ||
                                    answered?.id === suggestion.message.id)
                                    ? suggestion.text
                                    : undefined
                                }
                                onAnswer={(m) => reply(conversationMessages, m)}
                                onEdit={() =>
                                  setPicked(
                                    (current) =>
                                      current ??
                                      (answered
                                        ? pickEmail(
                                            conversationMessages,
                                            answered
                                          )
                                        : null)
                                  )
                                }
                                onSent={(sent) =>
                                  replied(row, conversationMessages, sent)
                                }
                              />
                            </div>
                          )}
                          {isUnknownSender(row) && hasInbound && (
                            <p className="mt-4 text-sm text-gray-600">
                              To reply, link this {quiet ? "email" : "number"}{" "}
                              to a student, parent or tutor, or add them: click
                              Unknown above.
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </RowFragment>
            );
          })}
        </tbody>
      </table>
      {listEnd}
    </>
  );
}

/** A table row and its details, as one keyed item. */
function RowFragment({ children }: { children: ReactNode }) {
  return <>{children}</>;
}
